import React, { useRef, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";

import "overlayscrollbars/css/OverlayScrollbars.css";

import "./scrollbar.override.css";
import styles from "./scrollbar.css";

export const SCROLLBAR_THICKNESS = {
  SMALL: "small",
  MEDIUM: "medium",
  BASE: "base"
};

function ScrollbarComponent(props) {
  const {
    children,
    className,
    style,
    onScroll,
    onScrollStart,
    onScrollStop,
    scrollRef,
    thickness = SCROLLBAR_THICKNESS.BASE,
    active = true,
    autoHide = false,
    scrollX = false,
    scrollY = true,
    sizeAutoCapable = true
  } = props;
  return active ? (
    <OverlayScrollbarsComponent
      ref={scrollRef}
      className={`${styles.scrollbar} ${className} ${styles[thickness]} ${
        autoHide ? "" : styles.fixedSpace
      }`}
      style={style}
      options={{
        paddingAbsolute: true,
        scrollbars: {
          clickScrolling: true,
          autoHide: autoHide ? "leave" : "never"
        },
        callbacks: {
          onScroll: onScroll,
          onScrollStart: onScrollStart,
          onScrollStop: onScrollStop
        },
        overflowBehavior: {
          x: scrollX ? "scroll" : "visible-hidden",
          y: scrollY ? "scroll" : "visible-hidden"
        },
        sizeAutoCapable: sizeAutoCapable
      }}
    >
      {children}
    </OverlayScrollbarsComponent>
  ) : (
    <div className={className}>{children}</div>
  );
}

ScrollbarComponent.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.shape({
    display: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  onScroll: PropTypes.func,
  onScrollStart: PropTypes.func,
  onScrollStop: PropTypes.func,
  scrollRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }).isRequired,
  active: PropTypes.bool,
  thickness: PropTypes.string,
  scrollX: PropTypes.bool,
  scrollY: PropTypes.bool,
  autoHide: PropTypes.bool,
  sizeAutoCapable: PropTypes.bool
};

/**
 * This component accepts a children and if the height is reached the content will be overflowed
 */
export const Scrollbar = forwardRef((props, ref) => {
  const scrollRef = useRef();
  useImperativeHandle(ref, () => {
    return {
      scrollToBottom: (animate) => {
        scrollRef &&
          scrollRef.current &&
          scrollRef.current
            .osInstance()
            .scroll(
              { y: "100%" },
              animate ? 1000 : 0,
              animate && "easeOutBounce"
            );
      },
      scrollToTop: (animate) => {
        scrollRef &&
          scrollRef.current &&
          scrollRef.current
            .osInstance()
            .scroll({ y: 0 }, animate ? 1000 : 0, animate && "easeOutBounce");
      },
      scrollToRight: (animate) => {
        scrollRef &&
          scrollRef.current &&
          scrollRef.current
            .osInstance()
            .scroll(
              { x: "100%" },
              animate ? 1000 : 0,
              animate && "easeOutBounce"
            );
      },
      scrollToLeft: (animate) => {
        scrollRef &&
          scrollRef.current &&
          scrollRef.current
            .osInstance()
            .scroll({ x: 0 }, animate ? 1000 : 0, animate && "easeOutBounce");
      },
      scroll: scrollRef.current.osInstance().scroll,
      target: scrollRef.current.osTarget()
    };
  });
  return <ScrollbarComponent scrollRef={scrollRef} {...props} />;
});

Scrollbar.propTypes = {
  /** A children node that is been render the component */
  children: PropTypes.node.isRequired,
  /** A class string will be passed */
  className: PropTypes.string,
  /** Inline style can be passed for any overridden */
  style: PropTypes.shape({
    display: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  /** Event will be fired when user scrolls inside the container */
  onScroll: PropTypes.func,
  /** Event will be fired when user scrolls get started inside the container */
  onScrollStart: PropTypes.func,
  /** Event will be fired when user scrolls get stopped inside the container */
  onScrollStop: PropTypes.func,
  /** A ref of the element */
  scrollRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }).isRequired,
  /** A prop when active overlay scrollbar is used else native scrollbar is used */
  active: PropTypes.bool,
  /** Thickness of the scroll thumb */
  thickness: PropTypes.oneOf(Object.values(SCROLLBAR_THICKNESS)),
  /** If enabled scrollthumb will hide after a timeout */
  autoHide: PropTypes.bool,
  /** If true horizontal scrollthumb is enabled */
  scrollX: PropTypes.bool,
  /** If true vertical scrollthumb is enabled */
  scrollY: PropTypes.bool
};

Scrollbar.defaultProps = {
  active: true,
  autoHide: false,
  scrollX: false,
  scrollY: true,
  sizeAutoCapable: true,
  thickness: SCROLLBAR_THICKNESS.BASE
};
